import React, { FC, ReactHTML } from 'react';
import { ContentLinkV2 } from 'components/ContentLinkV2/ContentLinkV2';
import { Image } from 'components/Image/Image';
import { TextV2 } from 'components/TextV2/TextV2';
import { TextWithArrow } from 'components/TextWithArrow/TextWithArrow';
import minusIcon from 'images/icons/minus-purple.svg';
import plusIcon from 'images/icons/plus-purple.svg';
import { toEntry } from 'services/cms/typesUtil';
import { useVerticalSystemContext } from '../VerticalSystemContext/VerticalSystemContext';
import { VerticalSystemTabItem, VerticalSystemVariant } from '../types';
import { FlatResponsiveImage } from 'components/FlatResponsiveImage/FlatResponsiveImage';

export type VerticalSystemTabPanelProps = {
  eyebrowTagName?: keyof ReactHTML;
  isListScrollable?: boolean;
  isTabActive?: boolean;
  styles?: Record<string, string>;
  tabItem: VerticalSystemTabItem;
  titleTagName?: keyof ReactHTML;
  onSelectVariant?: (activeVariant: VerticalSystemVariant) => void;
};

export const VerticalSystemTabPanel: FC<VerticalSystemTabPanelProps> = ({
  eyebrowTagName,
  isListScrollable,
  isTabActive,
  styles = {},
  tabItem,
  titleTagName,
  onSelectVariant,
}) => {
  const { activeVariant } = useVerticalSystemContext();

  return (
    <div
      aria-labelledby={`tab-${tabItem.id}`}
      className={styles.panel}
      hidden={!isTabActive}
      id={`panel-${tabItem.id}`}
      role="tabpanel"
    >
      <div>
        <ul
          className={styles.list}
          {...(isListScrollable && {
            tabIndex: 0,
            role: 'scrollbar',
            'aria-orientation': 'horizontal',
          })}
        >
          {tabItem.items.map(({ description, icon, itemTitle, link, title, variant }) => {
            const isVariantActive = activeVariant === variant;
            return (
              <li key={variant} className={styles.listItem}>
                <button
                  aria-controls={`sect-${tabItem.id}-${variant}`}
                  aria-expanded={isVariantActive}
                  className={styles.button}
                  id={`menu-${tabItem.id}-${variant}`}
                  type="button"
                  onClick={() => onSelectVariant?.(variant)}
                >
                  <TextV2
                    className={styles.eyebrow}
                    tagName={eyebrowTagName}
                    value={itemTitle}
                    variant="modern-eyebrow"
                  />
                  <div className={styles.titleGroup}>
                    <FlatResponsiveImage className={styles.icon} image={icon} isLazyLoad={false} />
                    <TextV2
                      className={styles.title}
                      tagName={titleTagName}
                      value={title}
                      variant={isVariantActive ? 'modern-body-large-medium' : 'modern-body-large'}
                    />
                    <Image
                      alt=""
                      className={styles.toggleIcon}
                      isLazyLoad={false}
                      src={!isListScrollable && isVariantActive ? minusIcon : plusIcon}
                      width={24}
                    />
                  </div>
                </button>
                <div
                  aria-labelledby={`menu-${tabItem.id}-${variant}`}
                  className={styles.region}
                  hidden={!isVariantActive}
                  id={`sect-${tabItem.id}-${variant}`}
                  role="region"
                >
                  <div className={styles.regionGroup}>
                    <TextV2
                      className={styles.description}
                      tagName="p"
                      value={description}
                      variant="modern-body"
                    />
                    {link && (
                      <ContentLinkV2 className={styles.link} entry={toEntry(link)} hideTitle>
                        <TextWithArrow theme="light-purple">{link.title}</TextWithArrow>
                      </ContentLinkV2>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        {tabItem.link && (
          <ContentLinkV2 className={styles.tabItemLink} entry={toEntry(tabItem.link)} hideTitle>
            <TextWithArrow theme="light-purple">{tabItem.link.title}</TextWithArrow>
          </ContentLinkV2>
        )}
      </div>
    </div>
  );
};
